(function () {
  'use strict';
  function calculate({ width, height, count, over, margin }) {
    if (![width, height, count, over, margin].every(Number.isFinite) ||
      !Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1 ||
      width > 10000 || height > 10000 || !Number.isInteger(count) || count < 1 || count > 100 ||
      ![1, 2].includes(over) || margin < 0 || margin > 25) return null;
    const designWidth = width * over / count;
    const designHeight = height * over / count;
    return { designWidth, designHeight, fabricWidth: designWidth + margin * 2,
      fabricHeight: designHeight + margin * 2, stitchesPerInch: count / over };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { calculate };
  if (typeof window === 'undefined' || customElements.get('pml-cross-stitch-calculator')) return;
  class CrossStitchCalculator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('form');
      this.messages = JSON.parse(this.querySelector('[data-messages]').textContent);
      this.unit = 'in';
      this.marginInches = 3;
      this.form.addEventListener('submit', event => { event.preventDefault(); this.update(true); });
      this.form.addEventListener('input', event => {
        if (event.target.name === 'margin') this.marginInches = event.target.value === '' ? NaN : Number(event.target.value) / this.factor;
        this.update();
      });
      this.form.addEventListener('change', event => {
        if (event.target.name === 'fabric') {
          const linen = event.target.value === 'evenweave';
          this.field('count').value = linen ? 28 : 14;
          this.field('over').value = linen ? 2 : 1;
          this.field('over').disabled = !linen;
          this.querySelector('[data-over-field]').hidden = !linen;
        }
        if (event.target.name === 'unit') {
          this.unit = event.target.value;
          const margin = this.field('margin');
          margin.max = String(25 * this.factor);
          margin.value = Number.isFinite(this.marginInches) ? String(Number((this.marginInches * this.factor).toFixed(6))) : '';
          this.querySelector('[data-margin-unit]').textContent = this.unit;
          this.querySelector('[data-margin-hint]').textContent = this.unit === 'in' ? this.messages.margin_in : this.messages.margin_cm;
        }
        this.update();
      });
      this.querySelector('[data-copy]').addEventListener('click', () => this.copy());
      this.querySelector('[data-print]').addEventListener('click', () => this.print());
      this.update();
    }
    get factor() { return this.unit === 'cm' ? 2.54 : 1; }
    field(name) { return this.form.elements.namedItem(name); }
    format(value) { return new Intl.NumberFormat(document.documentElement.lang || 'en', { maximumFractionDigits: 2 }).format(value); }
    size(width, height) { return `${this.format(width * this.factor)} × ${this.format(height * this.factor)} ${this.unit}`; }
    text(selector, value) { this.querySelector(selector).textContent = value; }
    update(announce = false) {
      clearTimeout(this.announceTimer);
      const number = name => this.field(name).value === '' ? NaN : Number(this.field(name).value);
      const settings = { width: number('width'), height: number('height'), count: number('count'),
        over: this.field('fabric').value === 'aida' ? 1 : number('over'), margin: this.marginInches };
      this.settings = settings;
      this.result = calculate(settings);
      const valid = Boolean(this.result) && this.form.checkValidity();
      this.querySelector('[data-error]').hidden = valid;
      this.querySelector('[data-results]').hidden = !valid;
      this.querySelector('[data-comparison]').hidden = !valid;
      for (const name of ['width', 'height', 'count', 'margin']) this.field(name).setAttribute('aria-invalid', String(!this.field(name).validity.valid));
      this.querySelector('[data-copy]').disabled = !valid;
      this.querySelector('[data-print]').disabled = !valid;
      this.text('[data-action-status]', '');
      if (!valid) { this.result = null; return; }
      const r = this.result;
      this.text('[data-design-size]', this.size(r.designWidth, r.designHeight));
      this.text('[data-fabric-size]', this.size(r.fabricWidth, r.fabricHeight));
      this.text('[data-buy-size]', `${Math.ceil(r.fabricWidth * this.factor - 1e-9)} × ${Math.ceil(r.fabricHeight * this.factor - 1e-9)} ${this.unit}`);
      this.text('[data-margin-result]', `${this.format(settings.margin * this.factor)} ${this.unit}`);
      this.text('[data-density]', `${this.format(r.stitchesPerInch)} ${this.messages.stitches_per_inch}`);
      this.text('[data-pattern-result]', `${settings.width} × ${settings.height}`);
      this.diagram();
      this.comparison();
      this.announceTimer = setTimeout(() => this.text('[data-live]', `${this.messages.finished}: ${this.size(r.designWidth, r.designHeight)}. ${this.messages.fabric_needed}: ${this.size(r.fabricWidth, r.fabricHeight)}.`), announce ? 0 : 650);
    }
    diagram() {
      const r = this.result;
      const scale = Math.min(320 / r.fabricWidth, 230 / r.fabricHeight);
      const fw = r.fabricWidth * scale, fh = r.fabricHeight * scale;
      const dw = r.designWidth * scale, dh = r.designHeight * scale;
      const svg = this.querySelector('[data-diagram]');
      const set = (name, values) => { const el = svg.querySelector(`[data-${name}]`); for (const [key, value] of Object.entries(values)) el.setAttribute(key, value); };
      set('fabric-rect', { x: 220 - fw / 2, y: 160 - fh / 2, width: fw, height: fh });
      set('design-rect', { x: 220 - dw / 2, y: 160 - dh / 2, width: dw, height: dh });
      set('width-line', { x1: 220 - fw / 2, x2: 220 + fw / 2, y1: 160 - fh / 2 - 17, y2: 160 - fh / 2 - 17 });
      set('width-label', { x: 220, y: 160 - fh / 2 - 27 });
      set('height-line', { x1: 220 + fw / 2 + 17, x2: 220 + fw / 2 + 17, y1: 160 - fh / 2, y2: 160 + fh / 2 });
      set('height-label', { transform: `translate(${220 + fw / 2 + 33},160) rotate(90)` });
      const heartSize = Math.min(dw, dh) * .65;
      set('heart', { transform: `translate(${220 - heartSize / 2},${160 - heartSize / 2}) scale(${heartSize / 100})` });
      this.text('[data-width-label]', `${this.format(r.fabricWidth * this.factor)} ${this.unit}`);
      this.text('[data-height-label]', `${this.format(r.fabricHeight * this.factor)} ${this.unit}`);
      svg.setAttribute('aria-label', `${this.messages.fabric_needed}: ${this.size(r.fabricWidth, r.fabricHeight)}. ${this.messages.finished}: ${this.size(r.designWidth, r.designHeight)}.`);
    }
    comparison() {
      const counts = this.field('fabric').value === 'aida' ? [11, 14, 16, 18, 20] : [25, 28, 32, 36, 40];
      if (!counts.includes(this.settings.count)) counts.push(this.settings.count);
      counts.sort((a, b) => a - b);
      const body = this.querySelector('[data-comparison-rows]');
      body.replaceChildren();
      for (const count of counts) {
        const r = calculate({ ...this.settings, count });
        const row = document.createElement('tr');
        if (count === this.settings.count) row.classList.add('is-selected');
        const label = document.createElement('th');
        label.scope = 'row';
        label.textContent = `${count} ${this.messages.count}${count === this.settings.count ? ` · ${this.messages.selected}` : ''}`;
        row.append(label);
        for (const value of [this.size(r.designWidth, r.designHeight), this.size(r.fabricWidth, r.fabricHeight)]) {
          const cell = document.createElement('td'); cell.textContent = value; row.append(cell);
        }
        body.append(row);
      }
      this.text('[data-comparison-note]', this.settings.over === 2 ? this.messages.over_two_note : this.messages.over_one_note);
    }
    summary() {
      const r = this.result, s = this.settings, m = this.messages;
      return [m.print_title, 'Paint Me Like',
        `${m.pattern}: ${s.width} × ${s.height} ${m.stitches}`,
        `${m.fabric_type}: ${this.field('fabric').selectedOptions[0].textContent}`,
        `${m.count}: ${s.count} · ${m.over}: ${s.over}`,
        `${m.finished}: ${this.size(r.designWidth, r.designHeight)}`,
        `${m.margin}: ${this.format(s.margin * this.factor)} ${this.unit}`,
        `${m.fabric_needed}: ${this.size(r.fabricWidth, r.fabricHeight)}`,
        `${m.rounded_cut}: ${this.querySelector('[data-buy-size]').textContent}`,
        m.print_note, 'paintmelike.co'].join('\n');
    }
    async copy() {
      if (!this.result) return;
      try { await navigator.clipboard.writeText(this.summary()); this.text('[data-action-status]', this.messages.copied); }
      catch { this.text('[data-action-status]', this.messages.copy_failed); }
    }
    print() {
      if (!this.result) return;
      const frame = document.createElement('iframe');
      frame.title = this.messages.print_title;
      frame.setAttribute('aria-hidden', 'true');
      frame.style.cssText = 'position:fixed;width:0;height:0;border:0;bottom:0;right:0';
      document.body.append(frame);
      const doc = frame.contentDocument;
      doc.open();
      doc.write('<!doctype html><html><head><meta charset="utf-8"><title></title><style>body{font:16px/1.7 Arial,sans-serif;color:#30223f;padding:36px}h1{font:36px Georgia,serif}pre{font:inherit;white-space:pre-wrap}svg{display:block;max-width:440px;width:100%;margin:25px auto}@page{margin:15mm}</style></head><body></body></html>');
      doc.close();
      doc.documentElement.lang = document.documentElement.lang || 'en';
      doc.title = this.messages.print_title;
      const heading = doc.createElement('h1'); heading.textContent = this.messages.print_title;
      const summary = doc.createElement('pre'); summary.textContent = this.summary();
      doc.body.append(heading, summary, this.querySelector('[data-diagram]').cloneNode(true));
      frame.contentWindow.addEventListener('afterprint', () => frame.remove(), { once: true });
      setTimeout(() => { frame.contentWindow.focus(); frame.contentWindow.print(); }, 150);
      setTimeout(() => frame.remove(), 300000);
    }
  }
  customElements.define('pml-cross-stitch-calculator', CrossStitchCalculator);
}());
